import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewChild, computed, effect, inject, linkedSignal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { map } from 'rxjs';
import pssData from '../../../json/pss-management.mock.json';
import generatorData from '../../../json/generators-management.mock.json';
import pathData from '../../../json/path-master.mock.json';
import contractData from '../../../json/contract-mapping.mock.json';
import pssDashboardData from '../../../json/pss-dashboard.mock.json';
import { DsmBlockService } from '../../core/services/dsm-block.service';
import Highcharts from 'highcharts';

type PssRow = typeof pssData.rows[number];
type GeneratorRow = typeof generatorData.rows[number];
type PathRow = typeof pathData.paths[number];
type ContractRow = typeof contractData.contractMappings[number];

@Component({
  selector: 'app-pss-dashboard',
  imports: [],
  templateUrl: './pss-dashboard.html',
  styleUrl: './pss-dashboard.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PssDashboard implements AfterViewInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly dsmBlockService = inject(DsmBlockService);
  readonly pssRows = pssData.rows as PssRow[];
  readonly generatorRows = generatorData.rows as GeneratorRow[];
  readonly pathRows = pathData.paths as PathRow[];
  readonly linkRows = pathData.links;
  readonly contractRows = contractData.contractMappings as ContractRow[];
  private readonly routePssCode = toSignal(
    this.route.paramMap.pipe(map((params) => params.get('id'))),
    { initialValue: this.route.snapshot.paramMap.get('id') }
  );
  readonly selectedPssCode = signal('PSS-001');
  readonly selectedDate = signal(pssDashboardData.defaultDate);
  readonly selectedTimeBlock = linkedSignal(() => this.dsmBlockService.currentBlockWindow());
  readonly timeBlockOptions = this.dsmBlockService.blockWindows;
  readonly appliedPssCode = signal(this.selectedPssCode());
  readonly revisionTrendResolution = signal<'hour' | 'block'>('hour');
  @ViewChild('revisionTrendChart') private revisionTrendChartElement?: ElementRef<HTMLElement>;
  @ViewChild('revisionTrendPanel') private revisionTrendPanelElement?: ElementRef<HTMLElement>;
  private revisionTrendChart?: Highcharts.Chart;
  private readonly onRevisionTrendFullscreenChange = (): void => {
    const isFullscreen = document.fullscreenElement === this.revisionTrendPanelElement?.nativeElement;
    this.refreshRevisionTrendChart();
    requestAnimationFrame(() => this.revisionTrendChart?.setSize(null, isFullscreen ? window.innerHeight - 88 : 200));
  };

  readonly pss = computed(() => this.pssRows.find((row) => row.code === this.appliedPssCode()) || this.pssRows[0]);
  readonly generators = computed(() => this.generatorRows.filter((row) => row.pssCode === this.pss().code));
  readonly paths = computed(() => this.pathRows.filter((row) => row.pssCode === this.pss().code));
  readonly links = computed(() => this.linkRows.filter((row) => row.pssCode === this.pss().code));
  readonly contracts = computed(() => this.contractRows.filter((row) => row.pssCode === this.pss().code));
  readonly flowAreas = computed(() => [...new Set(this.contracts().map((contract) => `${contract.buyerName} Flow Area`))]);
  readonly districts = computed(() => [...new Set(this.paths().flatMap((path) => path.districts))]);
  readonly profile = computed(() => pssDashboardData.profiles.find((profile) => profile.pssCode === this.pss().code) || pssDashboardData.profiles[0]);
  readonly cards = computed(() => [
    { label: 'Linked Generators', value: String(this.profile().cards.linkedGenerators), caption: `Active: ${this.profile().cards.linkedGenerators}`, icon: '⚡', tone: 'blue' },
    { label: 'Total Capacity (MW)', value: this.format(this.profile().cards.totalCapacityMw), caption: `Operational: ${this.profile().cards.totalCapacityMw - 149.5} MW`, icon: 'ϟ', tone: 'green' },
    { label: 'Total Links', value: String(this.profile().cards.totalLinks), caption: `Active: ${this.profile().cards.totalLinks}`, icon: '↗', tone: 'orange' },
    { label: 'Total Paths', value: String(this.profile().cards.totalPaths), caption: `Active: ${this.profile().cards.totalPaths}`, icon: '⌘', tone: 'purple' },
    { label: 'Total Contracts', value: String(this.profile().cards.totalContracts), caption: `Active: ${this.profile().cards.totalContracts}`, icon: '▤', tone: 'teal' }
  ]);

  constructor() {
    effect(() => {
      const pssCode = this.routePssCode();
      if (!pssCode || !this.pssRows.some((row) => row.code === pssCode)) return;

      this.selectedPssCode.set(pssCode);
      this.appliedPssCode.set(pssCode);
      queueMicrotask(() => this.refreshRevisionTrendChart());
    });

    effect(() => {
      this.dsmBlockService.currentBlock();
      this.refreshRevisionTrendChart();
    });
  }

  ngAfterViewInit(): void {
    this.createRevisionTrendChart();
    document.addEventListener('fullscreenchange', this.onRevisionTrendFullscreenChange);
  }
  ngOnDestroy(): void {
    this.revisionTrendChart?.destroy();
    document.removeEventListener('fullscreenchange', this.onRevisionTrendFullscreenChange);
  }
  applyFilters(): void { this.appliedPssCode.set(this.selectedPssCode()); queueMicrotask(() => this.refreshRevisionTrendChart()); }
  resetFilters(): void { this.selectedPssCode.set(this.routePssCode() || 'PSS-001'); this.selectedDate.set(pssDashboardData.defaultDate); this.selectedTimeBlock.set(this.dsmBlockService.currentBlockWindow()); this.applyFilters(); }
  onTimeBlockChange(value: string): void { this.selectedTimeBlock.set(value); }
  contractForPath(path: PathRow): ContractRow[] { return this.contracts().filter((contract) => path.contractIds.includes(contract.id)); }
  pathForLink(linkCode: string): PathRow | undefined { return this.paths().find((path) => path.linkCodes.includes(linkCode)); }
  bridgeSteps(count: number): number[] { return Array.from({ length: Math.max(count, 1) }, (_, index) => index); }
  bridgeStepsBetween(leftCount: number, rightCount: number): number[] { return this.bridgeSteps(Math.max(leftCount, rightCount)); }
  format(value: number): string { return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(value); }
  setRevisionTrendResolution(resolution: 'hour' | 'block'): void {
    if (this.revisionTrendResolution() === resolution) return;
    this.revisionTrendResolution.set(resolution);
    this.refreshRevisionTrendChart();
  }

  toggleRevisionTrendFullscreen(): void {
    const panel = this.revisionTrendPanelElement?.nativeElement;
    if (!panel) return;

    if (document.fullscreenElement === panel) {
      void document.exitFullscreen();
      return;
    }

    void panel.requestFullscreen?.();
  }

  private createRevisionTrendChart(): void {
    const target = this.revisionTrendChartElement?.nativeElement;
    if (!target) return;

    this.revisionTrendChart?.destroy();
    this.revisionTrendChart = Highcharts.chart(target, {
      chart: { type: 'spline', height: 200, backgroundColor: 'transparent', animation: false, margin: [18, 12, 68, 64], zooming: { type: 'x' }, panning: { enabled: true, type: 'x' }, panKey: 'shift', style: { fontFamily: 'var(--app-font-sans)' } },
      accessibility: { enabled: false }, credits: { enabled: false }, title: { text: undefined },
      xAxis: this.revisionTrendXAxisOptions(),
      yAxis: { title: { text: 'MW', margin: 12, style: { color: '#475569', fontSize: '10px' } }, gridLineColor: '#e9eef4', labels: { style: { color: '#64748b', fontSize: '9px' } } },
      tooltip: { shared: true, valueSuffix: ' MW' },
      legend: { align: 'center', verticalAlign: 'bottom', layout: 'horizontal', margin: 0, y: 15, itemDistance: 12, itemStyle: { color: '#000000', fontSize: '9px', fontWeight: '500' }, itemHiddenStyle: { color: '#94a3b8' } },
      plotOptions: { series: { marker: { enabled: true, radius: 2 }, states: { hover: { lineWidthPlus: 1 } } } },
      series: this.revisionTrendSeries(),
    });
  }

  private refreshRevisionTrendChart(): void {
    if (!this.revisionTrendChart) return;
    const series = this.revisionTrendSeries();
    this.revisionTrendChart.xAxis[0].update(this.revisionTrendXAxisOptions(), false);
    this.revisionTrendChart.series.forEach((chartSeries, index) => {
      chartSeries.setData((series[index] as Highcharts.SeriesSplineOptions).data ?? [], false);
    });
    this.revisionTrendChart.redraw();
  }

  private revisionTrendSeries(): Highcharts.SeriesOptionsType[] {
    const points = this.revisionTrendPoints();
    return [
      { type: 'spline', name: 'Schedule', color: '#2563eb', data: points.map((point) => point.schedule) },
      { type: 'spline', name: 'Actual', color: '#16a365', data: this.revisionTrendValues('actual') },
      { type: 'spline', name: 'Deviation', color: '#ef4444', data: this.revisionTrendValues('deviation') },
    ];
  }

  private revisionTrendPoints() {
    return this.profile().revisionTrend.block;
  }

  private revisionTrendValues(metric: 'actual' | 'deviation'): Array<number | null> {
    const lastCompletedBlockIndex = this.dsmBlockService.currentBlockNumber() - 2;
    return this.revisionTrendPoints().map((point, index) =>
      index <= lastCompletedBlockIndex ? point[metric] : null,
    );
  }

  private revisionTrendXAxisOptions(): Highcharts.XAxisOptions {
    const points = this.revisionTrendPoints();
    const isHourResolution = this.revisionTrendResolution() === 'hour';
    const isFullscreen = document.fullscreenElement === this.revisionTrendPanelElement?.nativeElement;
    const tickPositions = isHourResolution
      ? Array.from({ length: 24 }, (_, index) => index * 4)
      : isFullscreen
        ? Array.from({ length: 96 }, (_, index) => index)
        : Array.from({ length: 24 }, (_, index) => index * 4);

    return {
      categories: points.map((point) => isHourResolution ? point.time : String(point.block)),
      min: 0,
      max: points.length - 1,
      crosshair: { width: 1, color: '#9eb7df', dashStyle: 'Dash' },
      lineColor: '#dce4ee',
      tickLength: 0,
      tickPositions,
      labels: {
        autoRotation: [],
        rotation: isHourResolution ? -45 : isFullscreen ? 1 : 0,
        step: 1,
        allowOverlap: true,
        style: { color: '#64748b', fontSize: isFullscreen && !isHourResolution ? '8px' : '9px' },
        formatter: function () {
          const position = this.pos as number;
          return isHourResolution ? points[position]?.time ?? '' : String(position + 1);
        },
      },
    };
  }
}
